// src/components/TSOAuthDetector.tsx - Fixed redirect logic
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext.tsx';

const TSOAuthDetector: React.FC = () => {
  const { refreshProfile, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);
  
  useEffect(() => {
    // Only run this once
    if (hasChecked) return;
    
    const checkTSOToken = async () => {
      console.log('[TSO Auth] Starting token check for Layer Defender');
      
      try {
        // Skip if already authenticated
        if (isAuthenticated) {
        
          setHasChecked(true);
          return;
        }
        

        
        // Check URL for token parameter
        const urlParams = new URLSearchParams(location.search);
        const urlToken = urlParams.get('auth_token');
        
        // Check localStorage
        let tsoToken = localStorage.getItem('sb-auth-token');
        
        // If URL has token, use it
        if (urlToken) {
         
          tsoToken = urlToken;
          // Save to localStorage for future use
          localStorage.setItem('sb-auth-token', urlToken);
          
          // Clean up URL
          if (window.history && window.history.replaceState) {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }
        
        if (!tsoToken) {
      
          
          // REDIRECT TO ACCOUNTS SYSTEM IF NO TOKEN FOUND (Production only)
          const isLocalhost = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1' ||
                             window.location.hostname.includes('localhost');
          
          if (!isLocalhost) {
         
            const currentDomain = window.location.hostname;
            const accountsUrl = `https://accounts.${currentDomain.replace(/^[^.]+\./, '')}/profile`;
            
            // Give a small delay to ensure logging is visible
            setTimeout(() => {
              window.location.href = accountsUrl;
            }, 100);
            return;
          } else {
         
          }
          
          setHasChecked(true);
          return;
        }
        
       
        
        // Parse the token
        let tokenData;
        try {
          tokenData = JSON.parse(tsoToken);
      
        } catch (parseErr) {
         
          // Clear invalid token
          localStorage.removeItem('sb-auth-token');
          
          // REDIRECT TO ACCOUNTS SYSTEM IF TOKEN IS INVALID (Production only)
          const isLocalhost = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1' ||
                             window.location.hostname.includes('localhost');
          
          if (!isLocalhost) {
         
            const currentDomain = window.location.hostname;
            const accountsUrl = `https://accounts.${currentDomain.replace(/^[^.]+\./, '')}/profile`;
            
            setTimeout(() => {
              window.location.href = accountsUrl;
            }, 100);
            return;
          }
          
          setHasChecked(true);
          return;
        }
        
        // Set session with token
        if (tokenData?.access_token) {
    
          
          const { data, error } = await supabase.auth.setSession({
            access_token: tokenData.access_token,
            refresh_token: tokenData.refresh_token || ''
          });
          
          if (error) {
   
            
            // If token is invalid/expired, clear it and redirect
            if (error.message.includes('Invalid Refresh Token') || 
                error.message.includes('Already Used') ||
                error.message.includes('expired')) {
              localStorage.removeItem('sb-auth-token');
       
              
              // REDIRECT TO ACCOUNTS SYSTEM IF SESSION FAILED (Production only)
              const isLocalhost = window.location.hostname === 'localhost' || 
                                 window.location.hostname === '127.0.0.1' ||
                                 window.location.hostname.includes('localhost');
              
              if (!isLocalhost) {
               
                const currentDomain = window.location.hostname;
                const accountsUrl = `https://accounts.${currentDomain.replace(/^[^.]+\./, '')}/profile`;
                
                setTimeout(() => {
                  window.location.href = accountsUrl;
                }, 100);
                return;
              }
            }
            
            setHasChecked(true);
            return;
          }
          
  
          
          // Success - refresh the profile
          if (data?.session) {
            await refreshProfile();
            
            // Navigate to game if we're on the home page
            if (window.location.pathname === '/' || window.location.pathname === '') {
              navigate('/game');
            }
          }
        } else {
     
          
          // REDIRECT TO ACCOUNTS SYSTEM IF NO ACCESS TOKEN (Production only)
          const isLocalhost = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1' ||
                             window.location.hostname.includes('localhost');
          
          if (!isLocalhost) {
   
            const currentDomain = window.location.hostname;
            const accountsUrl = `https://accounts.${currentDomain.replace(/^[^.]+\./, '')}/profile`;
            
            setTimeout(() => {
              window.location.href = accountsUrl;
            }, 100);
            return;
          }
        }
      } catch (err: any) {

        
        // Clear any problematic tokens
        if (err.message?.includes('refresh') || err.message?.includes('token')) {
          localStorage.removeItem('sb-auth-token');
        }
        
        // REDIRECT TO ACCOUNTS SYSTEM ON UNEXPECTED ERROR (Production only)
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname.includes('localhost');
        
        if (!isLocalhost) {
       
          const currentDomain = window.location.hostname;
          const accountsUrl = `https://accounts.${currentDomain.replace(/^[^.]+\./, '')}/profile`;
          
          setTimeout(() => {
            window.location.href = accountsUrl;
          }, 100);
          return;
        }
      } finally {
        setHasChecked(true);
      }
    };

    checkTSOToken();
  }, [navigate, refreshProfile, isAuthenticated, hasChecked, location]);

  // This component doesn't render anything visible
  return null;
};

export default TSOAuthDetector;